import { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// Offline-first data layer
import { saveDataEntry, FORM_TYPES } from 'utils/offlineFirstDataLayer';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';

// ant design icons
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import FileSearchOutlined from '@ant-design/icons/FileSearchOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import FileOutlined from '@ant-design/icons/FileOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';

// project imports
import MainCard from 'components/MainCard';


const SessionPlanMode = ({
  viewMode = 'individual',
  onDataChange,
  childName = '',
  childId,
  therapistId,
  therapistName = '',
  sessionId
}) => {
  const theme = useTheme();
  const intl = useIntl();

  // Saving state to prevent duplicate saves
  const [saving, setSaving] = useState(false);

  // Session plan records
  const [sessionPlans, setSessionPlans] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    teachingPointArea: '',
    teacherActivity: '',
    studentActivity: '',
    tlm: '',
    tlmFiles: [],
    masteryCriteria: '',
    evaluation: ''
  });

  // Ref to skip auto-save on initial render
  const initialRenderRef = useRef(true);

  // Auto-save: notify parent when form data changes for debounced save to IndexedDB
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Skip if no meaningful data has been entered
    const hasFormData = formData.teachingPointArea || formData.teacherActivity || formData.studentActivity ||
      formData.tlm || formData.masteryCriteria || formData.evaluation || formData.tlmFiles.length > 0;
    const hasSessionPlans = sessionPlans.some(sp =>
      sp.teachingPointArea || sp.teacherActivity || sp.studentActivity ||
      sp.tlm || sp.masteryCriteria || sp.evaluation
    );
    if (!hasFormData && !hasSessionPlans) {
      return;
    }

    const autoSaveData = {
      sessionPlans: sessionPlans.map(sp => ({
        id: sp.id,
        teachingPointArea: sp.teachingPointArea,
        teacherActivity: sp.teacherActivity,
        studentActivity: sp.studentActivity,
        tlm: sp.tlm,
        tlmFiles: sp.tlmFiles || [],
        masteryCriteria: sp.masteryCriteria,
        evaluation: sp.evaluation
      })),
      currentForm: showAddForm ? {
        ...formData,
        editingRecordId: editingRecord?.id || null
      } : null
    };

    if (onDataChange) onDataChange('session-plan', autoSaveData);
  }, [sessionPlans, formData, showAddForm, editingRecord]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent of data change
  const notifyChange = useCallback(() => {
    if (onDataChange) onDataChange();
  }, [onDataChange]);

  // Reset form
  const resetForm = () => {
    setFormData({
      teachingPointArea: '',
      teacherActivity: '',
      studentActivity: '',
      tlm: '',
      tlmFiles: [],
      masteryCriteria: '',
      evaluation: ''
    });
    setEditingRecord(null);
    setShowAddForm(false);
  };

  // Handle form field changes
  const handleFormField = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    notifyChange();
  };

  // Handle file upload for TLM
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      file: file
    }));
    setFormData((prev) => ({
      ...prev,
      tlmFiles: [...prev.tlmFiles, ...newFiles]
    }));
    notifyChange();
    event.target.value = '';
  };

  // Remove uploaded file
  const handleRemoveFile = (fileId) => {
    setFormData((prev) => ({
      ...prev,
      tlmFiles: prev.tlmFiles.filter((f) => f.id !== fileId)
    }));
    notifyChange();
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Save session plan (add or update)
  const handleSave = async () => {
    if (saving) {
      return;
    }

    if (!formData.teachingPointArea) return;

    setSaving(true);

    try {
      const newPlan = { id: editingRecord?.id || Date.now(), ...formData };

      // Save to IndexedDB for offline-first support
      await saveDataEntry(
        FORM_TYPES.SESSION_PLAN,
        newPlan,
        {
          childId,
          childName,
          sessionId,
          therapistId,
          therapistName,
          sessionDate: new Date().toISOString()
        }
      );

      if (editingRecord) {
        setSessionPlans((prev) =>
          prev.map((sp) => (sp.id === editingRecord.id ? newPlan : sp))
        );
      } else {
        setSessionPlans((prev) => [newPlan, ...prev]);
      }
      resetForm();
      notifyChange();
    } catch (error) {
      console.error('[SessionPlanMode] Failed to save session plan:', error);
    } finally {
      setSaving(false);
    }
  };

  // Edit record
  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      teachingPointArea: record.teachingPointArea,
      teacherActivity: record.teacherActivity,
      studentActivity: record.studentActivity,
      tlm: record.tlm,
      tlmFiles: record.tlmFiles || [],
      masteryCriteria: record.masteryCriteria,
      evaluation: record.evaluation
    });
    setShowAddForm(true);
  };

  // Delete record
  const handleDelete = (id) => {
    setSessionPlans((prev) => prev.filter((sp) => sp.id !== id));
    notifyChange();
  };

  // Grid view inline edit
  const handleGridFieldChange = (id, field, value) => {
    setSessionPlans((prev) =>
      prev.map((sp) => (sp.id === id ? { ...sp, [field]: value } : sp))
    );
    notifyChange();
  };

  // Add empty row in grid view
  const handleAddGridRow = () => {
    const newPlan = {
      id: Date.now(),
      teachingPointArea: '',
      teacherActivity: '',
      studentActivity: '',
      tlm: '',
      tlmFiles: [],
      masteryCriteria: '',
      evaluation: ''
    };
    setSessionPlans((prev) => [newPlan, ...prev]);
    notifyChange();
  };

  // Section title with accent bar
  const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ width: 4, height: 20, borderRadius: 1, bgcolor: 'primary.main' }} />
      {children}
    </Typography>
  );

  // Field config for consistent rendering
  const FIELDS = [
    { key: 'teachingPointArea', label: intl.formatMessage({ id: 'form.sessionPlan.teachingPointArea' }), placeholder: intl.formatMessage({ id: 'form.sessionPlan.teachingPointPlaceholder' }), rows: 2, color: theme.palette.primary.main },
    { key: 'teacherActivity', label: intl.formatMessage({ id: 'form.sessionPlan.teacherActivity' }), placeholder: intl.formatMessage({ id: 'form.sessionPlan.teacherActivityPlaceholder' }), rows: 4, color: theme.palette.info.main },
    { key: 'studentActivity', label: intl.formatMessage({ id: 'form.sessionPlan.studentActivity' }), placeholder: intl.formatMessage({ id: 'form.sessionPlan.studentActivityPlaceholder' }), rows: 4, color: theme.palette.success.main },
    { key: 'tlm', label: intl.formatMessage({ id: 'form.sessionPlan.tlm' }), placeholder: intl.formatMessage({ id: 'form.sessionPlan.tlmPlaceholder' }), rows: 3, color: theme.palette.warning.main },
    { key: 'masteryCriteria', label: intl.formatMessage({ id: 'form.sessionPlan.masteryCriteria' }), placeholder: intl.formatMessage({ id: 'form.sessionPlan.masteryCriteriaPlaceholder' }), rows: 3, color: theme.palette.secondary.main },
    { key: 'evaluation', label: intl.formatMessage({ id: 'form.sessionPlan.evaluation' }), placeholder: intl.formatMessage({ id: 'form.sessionPlan.evaluationPlaceholder' }), rows: 3, color: theme.palette.error.main }
  ];

  // ──────────────── ADD / EDIT FORM ────────────────
  const renderForm = () => (
    <Paper sx={{ p: 3, border: 1, borderColor: 'divider', bgcolor: 'grey.50', '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}>
      <Stack spacing={3}>
        <SectionTitle>{intl.formatMessage({ id: 'form.sessionPlan.sessionPlanDetails' })}</SectionTitle>

        {/* Teaching Point / Area */}
        <TextField
          fullWidth
          label={intl.formatMessage({ id: 'form.sessionPlan.teachingPointArea' })}
          placeholder={intl.formatMessage({ id: 'form.sessionPlan.teachingPointPlaceholder' })}
          value={formData.teachingPointArea}
          onChange={handleFormField('teachingPointArea')}
          multiline
          rows={3}
        />

        {/* Teacher & Student Activities side by side */}
        <Box sx={{ display: 'flex', gap: 2.5, flexDirection: { xs: 'column', md: 'row' } }}>
          <TextField
            fullWidth
            label={intl.formatMessage({ id: 'form.sessionPlan.teacherActivity' })}
            placeholder={intl.formatMessage({ id: 'form.sessionPlan.teacherActivityPlaceholder' })}
            value={formData.teacherActivity}
            onChange={handleFormField('teacherActivity')}
            multiline
            rows={6}
            sx={{ flex: 1 }}
          />
          <TextField
            fullWidth
            label={intl.formatMessage({ id: 'form.sessionPlan.studentActivity' })}
            placeholder={intl.formatMessage({ id: 'form.sessionPlan.studentActivityPlaceholder' })}
            value={formData.studentActivity}
            onChange={handleFormField('studentActivity')}
            multiline
            rows={6}
            sx={{ flex: 1 }}
          />
        </Box>

        {/* TLM */}
        <Stack spacing={2}>
          <TextField
            fullWidth
            label={intl.formatMessage({ id: 'form.sessionPlan.tlm' })}
            placeholder={intl.formatMessage({ id: 'form.sessionPlan.tlmPlaceholder' })}
            value={formData.tlm}
            onChange={handleFormField('tlm')}
            multiline
            rows={4}
          />

          {/* File Upload Section */}
          <Box>
            <input
              type="file"
              id="tlm-file-upload"
              multiple
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
            />
            <label htmlFor="tlm-file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadOutlined />}
                sx={{ mb: 1 }}
              >
                {intl.formatMessage({ id: 'form.sessionPlan.uploadDocuments' })}
              </Button>
            </label>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              {intl.formatMessage({ id: 'form.sessionPlan.supportedFormats' })}
            </Typography>
          </Box>

          {/* Uploaded Files List */}
          {formData.tlmFiles.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.02)
              }}
            >
              <Typography variant="caption" fontWeight={600} color="primary.main" sx={{ mb: 1.5, display: 'block' }}>
                {intl.formatMessage({ id: 'form.sessionPlan.uploadedFiles' }, { count: formData.tlmFiles.length })}
              </Typography>
              <Stack spacing={1}>
                {formData.tlmFiles.map((file) => (
                  <Box
                    key={file.id}
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderRadius: 1.5,
                      bgcolor: 'background.paper',
                      border: `1px solid ${theme.palette.divider}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      }
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(theme.palette.primary.main, 0.1)
                        }}
                      >
                        <FileOutlined style={{ fontSize: '1.25rem', color: theme.palette.primary.main }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                    </Stack>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(file.id)}
                      sx={{
                        color: 'error.main',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.error.main, 0.12)
                        }
                      }}
                    >
                      <CloseCircleOutlined style={{ fontSize: '1.25rem' }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
        </Stack>

        {/* Mastery Criteria & Evaluation side by side */}
        <Box sx={{ display: 'flex', gap: 2.5, flexDirection: { xs: 'column', md: 'row' } }}>
          <TextField
            fullWidth
            label={intl.formatMessage({ id: 'form.sessionPlan.masteryCriteria' })}
            placeholder={intl.formatMessage({ id: 'form.sessionPlan.masteryCriteriaPlaceholder' })}
            value={formData.masteryCriteria}
            onChange={handleFormField('masteryCriteria')}
            multiline
            rows={5}
            sx={{ flex: 1 }}
          />
          <TextField
            fullWidth
            label={intl.formatMessage({ id: 'form.sessionPlan.evaluation' })}
            placeholder={intl.formatMessage({ id: 'form.sessionPlan.evaluationPlaceholder' })}
            value={formData.evaluation}
            onChange={handleFormField('evaluation')}
            multiline
            rows={5}
            sx={{ flex: 1 }}
          />
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={resetForm}>
            {intl.formatMessage({ id: 'form.sessionPlan.cancel' })}
          </Button>
          {editingRecord && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlined />}
              onClick={() => {
                handleDelete(editingRecord.id);
                resetForm();
              }}
            >
              {intl.formatMessage({ id: 'form.sessionPlan.delete' })}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<CheckOutlined />}
            onClick={handleSave}
            disabled={!formData.teachingPointArea || saving}
          >
            {saving ? intl.formatMessage({ id: 'form.sessionPlan.saving' }) : (editingRecord ? intl.formatMessage({ id: 'form.sessionPlan.update' }) : intl.formatMessage({ id: 'form.sessionPlan.save' }))}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );

  // ──────────────── INDIVIDUAL VIEW ────────────────
  const renderIndividualView = () => (
    <MainCard>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">{intl.formatMessage({ id: 'form.sessionPlan.sessionPlansCount' }, { count: sessionPlans.length })}</Typography>
          {!showAddForm && (
            <Button variant="contained" startIcon={<PlusOutlined />} onClick={() => setShowAddForm(true)}>
              {intl.formatMessage({ id: 'form.sessionPlan.new' })}
            </Button>
          )}
        </Stack>

        {showAddForm && renderForm()}

        {!showAddForm && sessionPlans.length === 0 ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <FileSearchOutlined style={{ fontSize: '3rem', color: theme.palette.grey[300] }} />
            <Typography variant="h6" color="text.secondary">{intl.formatMessage({ id: 'form.sessionPlan.noPlansYet' })}</Typography>
            <Typography variant="body2" color="text.secondary">{intl.formatMessage({ id: 'form.sessionPlan.clickNew' })}</Typography>
          </Stack>
        ) : !showAddForm ? (
          <Stack spacing={2}>
            {sessionPlans.map((sp) => (
              <Paper
                key={sp.id}
                elevation={0}
                sx={{
                  p: 2.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  '&:hover': { boxShadow: theme.shadows[2] }
                }}
              >
                <Stack spacing={2}>
                  {/* Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        flex: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        borderRadius: 1.5,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                        mr: 1
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ mb: 0.5, display: 'block' }}>
                        {intl.formatMessage({ id: 'form.sessionPlan.teachingPointSection' })}
                      </Typography>
                      <Typography variant="body2">{sp.teachingPointArea || '-'}</Typography>
                    </Paper>
                    <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                      <IconButton size="small" color="primary" onClick={() => handleEdit(sp)}>
                        <EditOutlined />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(sp.id)}>
                        <DeleteOutlined />
                      </IconButton>
                    </Stack>
                  </Stack>

                  {/* Teacher & Student Activity */}
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        flex: 1,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        borderRadius: 1.5,
                        border: `1px solid ${alpha(theme.palette.info.main, 0.12)}`
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="info.main" sx={{ mb: 0.5, display: 'block' }}>
                        {intl.formatMessage({ id: 'form.sessionPlan.teacherActivitySection' })}
                      </Typography>
                      <Typography variant="body2">{sp.teacherActivity || '-'}</Typography>
                    </Paper>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        flex: 1,
                        bgcolor: alpha(theme.palette.success.main, 0.08),
                        borderRadius: 1.5,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.12)}`
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="success.main" sx={{ mb: 0.5, display: 'block' }}>
                        {intl.formatMessage({ id: 'form.sessionPlan.studentActivitySection' })}
                      </Typography>
                      <Typography variant="body2">{sp.studentActivity || '-'}</Typography>
                    </Paper>
                  </Box>

                  {/* TLM */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.warning.main, 0.08),
                      borderRadius: 1.5,
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.12)}`
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="warning.dark" sx={{ mb: 0.5, display: 'block' }}>
                      {intl.formatMessage({ id: 'form.sessionPlan.tlmSection' })}
                    </Typography>
                    <Typography variant="body2">{sp.tlm || '-'}</Typography>
                    {sp.tlmFiles && sp.tlmFiles.length > 0 && (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                        {sp.tlmFiles.map((file) => (
                          <Chip
                            key={file.id}
                            icon={<FileOutlined />}
                            label={file.name}
                            size="small"
                            variant="outlined"
                            color="warning"
                            sx={{ mt: 0.5 }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Paper>

                  {/* Mastery Criteria & Evaluation */}
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        flex: 1,
                        bgcolor: alpha(theme.palette.secondary.main, 0.08),
                        borderRadius: 1.5,
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.12)}`
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: 'block', color: theme.palette.secondary.main }}>
                        {intl.formatMessage({ id: 'form.sessionPlan.masteryCriteriaSection' })}
                      </Typography>
                      <Typography variant="body2">{sp.masteryCriteria || '-'}</Typography>
                    </Paper>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        flex: 1,
                        bgcolor: alpha(theme.palette.error.main, 0.06),
                        borderRadius: 1.5,
                        border: `1px solid ${alpha(theme.palette.error.main, 0.12)}`
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="error.main" sx={{ mb: 0.5, display: 'block' }}>
                        {intl.formatMessage({ id: 'form.sessionPlan.evaluationSection' })}
                      </Typography>
                      <Typography variant="body2">{sp.evaluation || '-'}</Typography>
                    </Paper>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </MainCard>
  );

  // ──────────────── GRID VIEW ────────────────
  const renderGridView = () => (
    <MainCard
      title={intl.formatMessage({ id: 'form.sessionPlan.sessionPlans' })}
      secondary={
        <Button variant="contained" size="small" startIcon={<PlusOutlined />} onClick={handleAddGridRow}>
          {intl.formatMessage({ id: 'form.sessionPlan.addRecord' })}
        </Button>
      }
    >
      <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'hidden' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
              <TableCell sx={{ fontWeight: 600, width: '16%' }}>{intl.formatMessage({ id: 'form.sessionPlan.teachingPointHeader' })}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '19%' }}>{intl.formatMessage({ id: 'form.sessionPlan.teacherActivityHeader' })}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '19%' }}>{intl.formatMessage({ id: 'form.sessionPlan.studentActivityHeader' })}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '15%' }}>{intl.formatMessage({ id: 'form.sessionPlan.tlmHeader' })}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '15%' }}>{intl.formatMessage({ id: 'form.sessionPlan.masteryCriteriaHeader' })}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '12%' }}>{intl.formatMessage({ id: 'form.sessionPlan.evaluationHeader' })}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 48, p: 0.5 }} align="center">{intl.formatMessage({ id: 'form.sessionPlan.actions' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessionPlans.map((sp) => (
              <TableRow
                key={sp.id}
                hover
                sx={{ '&:hover .delete-btn': { opacity: 1 } }}
              >
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    value={sp.teachingPointArea}
                    onChange={(e) => handleGridFieldChange(sp.id, 'teachingPointArea', e.target.value)}
                    placeholder={intl.formatMessage({ id: 'form.sessionPlan.teachingPointGridPlaceholder' })}
                    variant="outlined"
                    sx={{ '& fieldset': { border: 'none' } }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    value={sp.teacherActivity}
                    onChange={(e) => handleGridFieldChange(sp.id, 'teacherActivity', e.target.value)}
                    placeholder={intl.formatMessage({ id: 'form.sessionPlan.teacherActivityGridPlaceholder' })}
                    variant="outlined"
                    sx={{ '& fieldset': { border: 'none' } }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    value={sp.studentActivity}
                    onChange={(e) => handleGridFieldChange(sp.id, 'studentActivity', e.target.value)}
                    placeholder={intl.formatMessage({ id: 'form.sessionPlan.studentActivityGridPlaceholder' })}
                    variant="outlined"
                    sx={{ '& fieldset': { border: 'none' } }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    value={sp.tlm}
                    onChange={(e) => handleGridFieldChange(sp.id, 'tlm', e.target.value)}
                    placeholder={intl.formatMessage({ id: 'form.sessionPlan.tlmGridPlaceholder' })}
                    variant="outlined"
                    sx={{ '& fieldset': { border: 'none' } }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    value={sp.masteryCriteria}
                    onChange={(e) => handleGridFieldChange(sp.id, 'masteryCriteria', e.target.value)}
                    placeholder={intl.formatMessage({ id: 'form.sessionPlan.masteryCriteriaGridPlaceholder' })}
                    variant="outlined"
                    sx={{ '& fieldset': { border: 'none' } }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    value={sp.evaluation}
                    onChange={(e) => handleGridFieldChange(sp.id, 'evaluation', e.target.value)}
                    placeholder={intl.formatMessage({ id: 'form.sessionPlan.evaluationGridPlaceholder' })}
                    variant="outlined"
                    sx={{ '& fieldset': { border: 'none' } }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', p: 0.5 }} align="center">
                  <IconButton
                    size="small"
                    color="error"
                    className="delete-btn"
                    onClick={() => handleDelete(sp.id)}
                    sx={{ opacity: 0, transition: 'opacity 0.2s' }}
                  >
                    <DeleteOutlined />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </MainCard>
  );

  return viewMode === 'individual' ? renderIndividualView() : renderGridView();
};

SessionPlanMode.propTypes = {
  viewMode: PropTypes.string,
  onDataChange: PropTypes.func,
  childName: PropTypes.string,
  childId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistName: PropTypes.string
};

export default SessionPlanMode;
